import { useCallback, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  useAdminSubawardeeFilterStore,
  isKnownSubawardee,
} from '../stores/adminSubawardeeFilterStore';
import { isAdminSubawardeeFilterListRoute } from '../utils/adminSubawardeePaths';

/**
 * Shared admin subawardee filter: persisted (Zustand), synced with `?subawardee=` on reports/milestones
 * list routes so the same selection applies on both pages and survives navigation + refresh.
 */
export function useAdminSubawardeeUrlState(): [string | null, (value: string | null) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const subawardee = useAdminSubawardeeFilterStore((s) => s.subawardee);
  const setSubawardeeStore = useAdminSubawardeeFilterStore((s) => s.setSubawardee);

  // Deep link / browser history: valid ?subawardee= in URL updates the shared store
  useEffect(() => {
    const fromUrl = searchParams.get('subawardee');
    if (isKnownSubawardee(fromUrl) && fromUrl !== subawardee) {
      setSubawardeeStore(fromUrl);
    }
  }, [searchParams, subawardee, setSubawardeeStore]);

  // List route with no query but store has a selection — add ?subawardee= (e.g. navbar link before store synced URL)
  useEffect(() => {
    if (!isAdminSubawardeeFilterListRoute(location.pathname)) return;
    const fromUrl = searchParams.get('subawardee');
    if (fromUrl) return;
    if (isKnownSubawardee(subawardee)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('subawardee', subawardee!);
          return next;
        },
        { replace: true }
      );
    }
  }, [subawardee, location.pathname, searchParams, setSearchParams]);

  const setSubawardee = useCallback(
    (value: string | null) => {
      setSubawardeeStore(value);
      if (isAdminSubawardeeFilterListRoute(location.pathname)) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            if (value && isKnownSubawardee(value)) {
              next.set('subawardee', value);
            } else {
              next.delete('subawardee');
            }
            return next;
          },
          { replace: true }
        );
      }
    },
    [location.pathname, setSearchParams, setSubawardeeStore]
  );

  return [subawardee, setSubawardee];
}
